import {ComponentRef, Type} from '@angular/core';
import {VotingControllerComponent} from '@modules/voting/voting-controller/voting-controller.component';
import {EventsHttpService} from '@api/http/events/events-http.service';
import {Event} from '@api/models/event/event';
import {AppPromotionComponent} from '@shared/components/promotions/app-promotion/app-promotion.component';
import {AdComponent} from '@lib-modules/ads/components/ad/ad.component';
import {GamesListComponent} from '@components/games/components/games-list/games-list.component';
import {FollowBannerComponent} from '@shared/components/league-follow-banner/follow-banner.component';
import {AppStoreButtonsComponent} from '@components/app-store-buttons/app-store-buttons.component';
import {NewsRelatedArticlesComponent} from '@shared/modules/shared-news/components/news-related-articles/news-related-articles.component';
import {INewsPost} from '@api/models/news/news';
import {FollowEntityType} from '@api/models/user/user-follow';
import { ColorScheme } from '@lib/models/color-scheme/color-scheme';
import { NewsEmbedButtonComponent } from '../components/news-embed-button/news-embed-button.component';
import {EntityType} from '@lib/models/entity/entity-type';

export enum EmbedType {
  Voting = 'voting',
  MatchTeaser = 'match-teaser',
  AppPromotion = 'app-promo',
  GoogleAd = 'google-ad',
  FollowBanner = 'follow-banner',
  AppStoreButtons = 'app-store-buttons',
  RelatedNews = 'related-news',
  LinkButton = 'link-button'
}

export abstract class DefaultEmbedConfig<T> {
  abstract type: EmbedType;
  associatedComponentType: Type<T>;
  placeholderElement: HTMLElement;
  parentNews: INewsPost // for now, embedding is limited to news

  async initComponent(ref: ComponentRef<T>) {};

  afterInit(ref: ComponentRef<T>) {
    ref.changeDetectorRef.detectChanges();
  }
}

export class VotingEmbedConfig extends DefaultEmbedConfig<VotingControllerComponent> {
  type: EmbedType.Voting;
  associatedComponentType = VotingControllerComponent;
  id: string;

  async initComponent(ref: ComponentRef<VotingControllerComponent>): Promise<void> {
    await super.initComponent(ref);
    ref.instance.votingId = this.id;
    ref.instance.setMetadata = true;
    await ref.instance.checkInitFromInput();

    this.afterInit(ref);
  }
}

export class MatchTeaserEmbedConfig extends DefaultEmbedConfig<GamesListComponent> {
  type: EmbedType.MatchTeaser;
  associatedComponentType = GamesListComponent;
  id: string;
  eventService: EventsHttpService;

  async initComponent(ref: ComponentRef<GamesListComponent>): Promise<void> {
    await super.initComponent(ref);

    const event = new Event(await this.eventService.getEvent(this.id));
    ref.instance.events = [event];
    ref.instance.openLinksInNewTab = false;
    ref.instance.highlightOnHover = false;
    ref.instance.showStatus = true;
    ref.instance.live = true;
    ref.instance.spaced = false;
    ref.instance.handleChanges();
    ref.instance.colorScheme = ColorScheme.DarkBlue;

    this.afterInit(ref);
  }
}


export class AppPromotionEmbedConfig extends DefaultEmbedConfig<AppPromotionComponent> {
  type: EmbedType.AppPromotion;
  associatedComponentType = AppPromotionComponent;

  async initComponent(ref: ComponentRef<AppPromotionComponent>) {
    await super.initComponent(ref);
    super.afterInit(ref);
  }
}

export class GoogleAdEmbedConfig extends DefaultEmbedConfig<AdComponent> {
  type: EmbedType.GoogleAd;
  associatedComponentType = AdComponent;

  async initComponent(ref: ComponentRef<AdComponent>) {
    await super.initComponent(ref);
    super.afterInit(ref);
  }
}

export class FollowBannerEmbedConfig extends DefaultEmbedConfig<FollowBannerComponent> {
  type: EmbedType.FollowBanner;
  entityReadableId: string;
  entityType: EntityType;
  associatedComponentType = FollowBannerComponent;

  async initComponent(ref: ComponentRef<FollowBannerComponent>) {
    await super.initComponent(ref);
    ref.instance.entityType = this.entityType;
    ref.instance.entityReadableId = this.entityReadableId ?? ref.instance.entityReadableId;
    super.afterInit(ref);
  }
}

export class AppStoreButtonsEmbedConfig extends DefaultEmbedConfig<AppStoreButtonsComponent> {
  type: EmbedType.AppStoreButtons;
  associatedComponentType = AppStoreButtonsComponent;

  async initComponent(ref: ComponentRef<AppStoreButtonsComponent>) {
    await super.initComponent(ref);
    super.afterInit(ref);
  }
}

export class RelatedNewsEmbedConfig extends DefaultEmbedConfig<NewsRelatedArticlesComponent> {
  type: EmbedType.RelatedNews;
  count: number;
  relatedArticleIds: string; // expected as comma seperated list-string
  associatedComponentType = NewsRelatedArticlesComponent;

  async initComponent(ref: ComponentRef<NewsRelatedArticlesComponent>) {
    await super.initComponent(ref);
    ref.instance.count = this.count || ref.instance.count;
    ref.instance.relatedArticleIds = this.relatedArticleIds?.replace(' ', '').split(',');
    ref.instance.relationBaseId = this.parentNews._id;

    super.afterInit(ref);
  }
}

export class LinkButtonEmbedConfig extends DefaultEmbedConfig<NewsEmbedButtonComponent> {
  type: EmbedType.LinkButton;
  text: string;
  url: string;
  color: string; // Hex value
  background: string; // Hex value
  associatedComponentType = NewsEmbedButtonComponent;

  async initComponent(ref: ComponentRef<NewsEmbedButtonComponent>) {
    await super.initComponent(ref);
    ref.instance.text = this.text;
    ref.instance.url = this.url;
    ref.instance.color = this.color || ref.instance.color;
    ref.instance.background = this.background || ref.instance.background;

    super.afterInit(ref);
  }
}
